/* Phase 68.4 — AutoScout24-style mobile quick filters.
 *
 * Each filter chip opens its OWN lightweight inline popover (NOT the full drawer).
 *  - Marke: shadcn Command searchable list of BRANDS (sticky search)
 *  - Modell: dependent Command list of models for current brand
 *  - Preis: dual min/max number inputs + 4 preset CHF buckets
 *  - Kilometer: single max-km input + 5 preset buckets
 *  - Treibstoff: chip-grid (benzin / diesel / elektro / hybrid)
 *  - Mehr Filter: opens the FULL drawer via prop
 *
 * Plus a primary "Suche speichern"-action button at the right of the bar.
 */
import React, { useState, useMemo, useEffect } from "react";
import { ChevronDown, X, Check, BellPlus } from "lucide-react";
import { BRANDS, FUELS } from "../lib/constants";
import { getModelsForBrand } from "../lib/brandModels";
import { POPULAR_BRANDS_CH, POPULAR_MODELS_BY_BRAND, getRecent, pushRecent } from "../lib/recentPicks";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const PRICE_PRESETS = [
  { label: "bis 10’000", max: 10000 },
  { label: "bis 25’000", max: 25000 },
  { label: "bis 50’000", max: 50000 },
  { label: "bis 100’000", max: 100000 },
];
const KM_PRESETS = [25000, 50000, 75000, 100000, 150000, 200000];

export default function MobileQuickFilters({
  get,                  // (key) => string current URL param value
  setMany,              // ({k:v, ...}) => void  updates URL params
  total,                // current result count
  chipsCount = 0,       // number of active chips (for "Mehr Filter" badge)
  onOpenDrawer,         // () => void  opens the full filter drawer
  onSaveSearch,         // () => void  saves current search
  saveSearchBusy = false,
  saveSearchLabel = "Suche speichern",
  moreFiltersLabel = "Mehr Filter",
}) {
  const [openKey, setOpenKey] = useState(null); // 'brand' | 'model' | 'price' | 'km' | 'fuel' | null

  const brand = get("brand") || "";
  const model = get("model") || "";
  const minPrice = get("min_price") || "";
  const maxPrice = get("max_price") || "";
  const maxKm = get("max_km") || "";
  const fuel = get("fuel") || "";

  const close = () => setOpenKey(null);

  return (
    <div className="-mx-4 mt-5 lg:hidden" data-testid="mobile-quick-filters">
      <div className="flex items-center gap-2 overflow-x-auto px-4 pb-1">
        {/* MARKE */}
        <QuickPopover
          open={openKey === "brand"}
          onOpenChange={(o) => setOpenKey(o ? "brand" : null)}
          label="Marke"
          value={brand}
          testId="quick-filter-brand"
          contentWidth="w-[88vw] max-w-sm"
        >
          <BrandPicker
            value={brand}
            onPick={(b) => { setMany({ brand: b, model: "" }); pushRecent("brand", b); close(); }}
            onClear={() => { setMany({ brand: "", model: "" }); close(); }}
          />
        </QuickPopover>

        {/* MODELL */}
        <QuickPopover
          open={openKey === "model"}
          onOpenChange={(o) => setOpenKey(o ? "model" : null)}
          label="Modell"
          value={model}
          disabled={!brand}
          testId="quick-filter-model"
          contentWidth="w-[88vw] max-w-sm"
        >
          <ModelPicker
            brand={brand}
            value={model}
            onPick={(m) => { setMany({ model: m }); if (brand) pushRecent(`model:${brand}`, m); close(); }}
            onClear={() => { setMany({ model: "" }); close(); }}
          />
        </QuickPopover>

        {/* PREIS */}
        <QuickPopover
          open={openKey === "price"}
          onOpenChange={(o) => setOpenKey(o ? "price" : null)}
          label="Preis"
          value={(minPrice || maxPrice) ? `CHF ${minPrice || 0}–${maxPrice || "∞"}` : ""}
          testId="quick-filter-price"
          contentWidth="w-[88vw] max-w-sm"
        >
          <PricePicker
            min={minPrice}
            max={maxPrice}
            onApply={(mn, mx) => { setMany({ min_price: mn, max_price: mx }); close(); }}
            onClear={() => { setMany({ min_price: "", max_price: "" }); close(); }}
          />
        </QuickPopover>

        {/* KILOMETER */}
        <QuickPopover
          open={openKey === "km"}
          onOpenChange={(o) => setOpenKey(o ? "km" : null)}
          label="Kilometer"
          value={maxKm ? `≤ ${Number(maxKm).toLocaleString("de-CH")}` : ""}
          testId="quick-filter-km"
          contentWidth="w-[88vw] max-w-sm"
        >
          <KmPicker
            value={maxKm}
            onApply={(v) => { setMany({ max_km: v }); close(); }}
            onClear={() => { setMany({ max_km: "" }); close(); }}
          />
        </QuickPopover>

        {/* TREIBSTOFF */}
        <QuickPopover
          open={openKey === "fuel"}
          onOpenChange={(o) => setOpenKey(o ? "fuel" : null)}
          label="Treibstoff"
          value={fuel}
          testId="quick-filter-fuel"
          contentWidth="w-[88vw] max-w-xs"
        >
          <FuelPicker
            value={fuel}
            onPick={(f) => { setMany({ fuel: f }); close(); }}
            onClear={() => { setMany({ fuel: "" }); close(); }}
          />
        </QuickPopover>

        {/* MEHR FILTER chip removed (Phase 68.5) — the single "Filter" button
            in the page header already opens the same drawer. */}

        {/* SUCHE SPEICHERN — primary right-aligned action */}
        {onSaveSearch && (
          <button
            type="button"
            onClick={onSaveSearch}
            disabled={saveSearchBusy}
            data-testid="quick-action-save-search"
            className="ml-auto flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border border-red-500/40 bg-gradient-to-r from-red-600/15 to-orange-500/10 px-4 py-2 text-sm font-semibold text-red-200 hover:from-red-600/25 hover:to-orange-500/20 hover:text-white disabled:opacity-50"
          >
            <BellPlus className="h-4 w-4" />
            <span>{saveSearchLabel}</span>
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Generic popover wrapper for quick filter chips ─────────────────── */
function QuickPopover({ open, onOpenChange, label, value, disabled = false, testId, contentWidth = "w-64", children }) {
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && onOpenChange(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          aria-expanded={open}
          className={`flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            value
              ? "border-[var(--ch-red)]/60 bg-[var(--ch-red)]/10 text-white"
              : "border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-zinc-300 hover:border-zinc-700 hover:text-white"
          }`}
        >
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
          {value && <span className="text-[13px] text-white max-w-[120px] truncate">{value}</span>}
          <ChevronDown className={`h-3.5 w-3.5 opacity-60 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={`${contentWidth} border-[var(--ch-line)] bg-[var(--ch-bg-0)] p-0 text-white`}
        align="start"
        sideOffset={6}
        data-testid={`${testId}-popover`}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

/* ─── Brand searchable popover ───────────────────────────────────────── */
function BrandPicker({ value, onPick, onClear }) {
  const recent = useMemo(() => getRecent("brand").filter((b) => BRANDS.includes(b)), []);
  return (
    <Command>
      <CommandInput placeholder="Marke suchen…" className="h-11" data-testid="quick-brand-search" />
      <CommandList className="max-h-[320px]" onWheel={(e) => e.stopPropagation()}>
        <CommandEmpty>Keine Marke gefunden</CommandEmpty>
        {value && (
          <>
            <CommandGroup>
              <CommandItem value="__clear__" onSelect={onClear} className="cursor-pointer text-red-300 data-[selected=true]:bg-zinc-800" data-testid="quick-brand-clear">
                <X className="mr-2 h-3.5 w-3.5" /> Marke zurücksetzen
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        {recent.length > 0 && (
          <>
            <CommandGroup heading="Zuletzt">
              {recent.map((b) => (
                <CommandItem key={`r-${b}`} value={b} onSelect={onPick} className="cursor-pointer data-[selected=true]:bg-zinc-800" data-testid={`quick-brand-recent-${b}`}>
                  <span className="flex-1 truncate">{b}</span>
                  {value === b && <Check className="ml-2 h-4 w-4 text-red-500" />}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        <CommandGroup heading="Häufig gewählt">
          {POPULAR_BRANDS_CH.filter((b) => BRANDS.includes(b) && !recent.includes(b)).map((b) => (
            <CommandItem key={`p-${b}`} value={b} onSelect={onPick} className="cursor-pointer data-[selected=true]:bg-zinc-800" data-testid={`quick-brand-popular-${b}`}>
              <span className="flex-1 truncate">{b}</span>
              {value === b && <Check className="ml-2 h-4 w-4 text-red-500" />}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Alle Marken">
          {BRANDS.filter((b) => !recent.includes(b) && !POPULAR_BRANDS_CH.includes(b)).map((b) => (
            <CommandItem key={b} value={b} onSelect={onPick} className="cursor-pointer data-[selected=true]:bg-zinc-800" data-testid={`quick-brand-item-${b}`}>
              <span className="flex-1 truncate">{b}</span>
              {value === b && <Check className="ml-2 h-4 w-4 text-red-500" />}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

/* ─── Model searchable popover (dependent on brand) ──────────────────── */
function ModelPicker({ brand, value, onPick, onClear }) {
  const models = useMemo(() => getModelsForBrand(brand), [brand]);
  const recent = useMemo(() => brand ? getRecent(`model:${brand}`).filter((m) => models.includes(m)) : [], [brand, models]);
  const popular = useMemo(() => (POPULAR_MODELS_BY_BRAND[brand] || []).filter((m) => models.includes(m)), [brand, models]);
  if (!brand) {
    return <div className="p-6 text-center text-sm text-zinc-400">Bitte zuerst Marke wählen</div>;
  }
  return (
    <Command>
      <CommandInput placeholder="Modell suchen…" className="h-11" data-testid="quick-model-search" />
      <CommandList className="max-h-[320px]" onWheel={(e) => e.stopPropagation()}>
        <CommandEmpty>Kein Modell gefunden</CommandEmpty>
        {value && (
          <>
            <CommandGroup>
              <CommandItem value="__clear__" onSelect={onClear} className="cursor-pointer text-red-300 data-[selected=true]:bg-zinc-800" data-testid="quick-model-clear">
                <X className="mr-2 h-3.5 w-3.5" /> Modell zurücksetzen
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        {recent.length > 0 && (
          <>
            <CommandGroup heading="Zuletzt">
              {recent.map((m) => (
                <CommandItem key={`r-${m}`} value={m} onSelect={onPick} className="cursor-pointer data-[selected=true]:bg-zinc-800">
                  <span className="flex-1 truncate">{m}</span>
                  {value === m && <Check className="ml-2 h-4 w-4 text-red-500" />}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        {popular.length > 0 && (
          <>
            <CommandGroup heading="Häufig">
              {popular.filter((m) => !recent.includes(m)).map((m) => (
                <CommandItem key={`p-${m}`} value={m} onSelect={onPick} className="cursor-pointer data-[selected=true]:bg-zinc-800">
                  <span className="flex-1 truncate">{m}</span>
                  {value === m && <Check className="ml-2 h-4 w-4 text-red-500" />}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        <CommandGroup heading="Alle Modelle">
          {models.filter((m) => !recent.includes(m) && !popular.includes(m)).map((m) => (
            <CommandItem key={m} value={m} onSelect={onPick} className="cursor-pointer data-[selected=true]:bg-zinc-800">
              <span className="flex-1 truncate">{m}</span>
              {value === m && <Check className="ml-2 h-4 w-4 text-red-500" />}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

/* ─── Price quick picker ─────────────────────────────────────────────── */
function PricePicker({ min, max, onApply, onClear }) {
  const [mn, setMn] = useState(min || "");
  const [mx, setMx] = useState(max || "");
  useEffect(() => { setMn(min || ""); setMx(max || ""); }, [min, max]);
  return (
    <div className="p-4 space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Preis (CHF)</div>
      <div className="grid grid-cols-2 gap-2">
        <Input type="number" inputMode="numeric" value={mn} onChange={(e) => setMn(e.target.value)} placeholder="Min" className="h-11 border-zinc-800 bg-zinc-950" data-testid="quick-price-min" />
        <Input type="number" inputMode="numeric" value={mx} onChange={(e) => setMx(e.target.value)} placeholder="Max" className="h-11 border-zinc-800 bg-zinc-950" data-testid="quick-price-max" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {PRICE_PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => { setMn(""); setMx(String(p.max)); }}
            data-testid={`quick-price-preset-${p.max}`}
            className="rounded-full border border-zinc-800 bg-zinc-950 px-3 py-1 text-xs text-zinc-300 hover:border-red-500/40 hover:text-white"
          >{p.label}</button>
        ))}
      </div>
      <div className="flex gap-2 pt-2">
        <Button size="sm" onClick={() => onApply(mn, mx)} className="flex-1 bg-red-600 text-white hover:bg-red-500 h-11" data-testid="quick-price-apply">Anwenden</Button>
        {(min || max) && (
          <Button size="sm" variant="outline" onClick={onClear} className="h-11 border-zinc-800 text-zinc-300" data-testid="quick-price-clear">Zurücksetzen</Button>
        )}
      </div>
    </div>
  );
}

/* ─── Kilometer quick picker ─────────────────────────────────────────── */
function KmPicker({ value, onApply, onClear }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <div className="p-4 space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Max. Kilometer</div>
      <Input type="number" inputMode="numeric" value={v} onChange={(e) => setV(e.target.value)} placeholder="z.B. 80’000" className="h-11 border-zinc-800 bg-zinc-950" data-testid="quick-km-input" />
      <div className="flex flex-wrap gap-1.5">
        {KM_PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setV(String(p))}
            data-testid={`quick-km-preset-${p}`}
            className={`rounded-full border px-3 py-1 text-xs ${
              String(v) === String(p)
                ? "border-red-500/60 bg-red-600/10 text-white"
                : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-red-500/40 hover:text-white"
            }`}
          >≤ {p.toLocaleString("de-CH")}</button>
        ))}
      </div>
      <div className="flex gap-2 pt-2">
        <Button size="sm" onClick={() => onApply(v)} className="flex-1 bg-red-600 text-white hover:bg-red-500 h-11" data-testid="quick-km-apply">Anwenden</Button>
        {value && (
          <Button size="sm" variant="outline" onClick={onClear} className="h-11 border-zinc-800 text-zinc-300" data-testid="quick-km-clear">Zurücksetzen</Button>
        )}
      </div>
    </div>
  );
}

/* ─── Fuel chip-grid picker ──────────────────────────────────────────── */
function FuelPicker({ value, onPick, onClear }) {
  return (
    <div className="p-4 space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Treibstoff</div>
      <div className="grid grid-cols-2 gap-2">
        {FUELS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => onPick(f)}
            data-testid={`quick-fuel-${f}`}
            className={`flex min-h-[44px] items-center justify-center rounded-lg border px-3 py-2 text-sm capitalize ${
              value === f
                ? "border-red-500/60 bg-red-600/10 text-white"
                : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-red-500/40 hover:text-white"
            }`}
          >{f}</button>
        ))}
      </div>
      {value && (
        <Button size="sm" variant="outline" onClick={onClear} className="w-full h-11 border-zinc-800 text-zinc-300" data-testid="quick-fuel-clear">Zurücksetzen</Button>
      )}
    </div>
  );
}
